import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { ArrowLeft, Mail, Phone, Globe, UserRound, Edit, Trash2, X, Save, KeyRound, Shield, ShieldOff, ShieldCheck } from "lucide-react";
import ConfirmDialog from "../components/ConfirmDialog";
import { usePermissions } from "../lib/permissions";
import { useAuth } from "../context/AuthContext";

const FULL_PORTAL_ROLES = new Set(["super_admin", "admin", "manager"]);

const statusToBadge = (s) => {
  switch (s) {
    case "active":   return "se-badge se-badge-active";
    case "injured":  return "se-badge se-badge-urgent";
    case "retired":  return "se-badge se-badge-inactive";
    case "sold":     return "se-badge se-badge-inactive";
    default:         return "se-badge se-badge-inactive";
  }
};

export default function OwnerProfile() {
  const { ownerId } = useParams();
  const navigate = useNavigate();
  const { canEdit, canDelete } = usePermissions();
  const { user: me } = useAuth();
  const canManagePortal = FULL_PORTAL_ROLES.has(me?.role);
  const [owner, setOwner] = useState(null);
  const [horses, setHorses] = useState(null);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [confirmDel, setConfirmDel] = useState(false);
  const [deleteWarning, setDeleteWarning] = useState("");
  const [toast, setToast] = useState("");
  const [showGrant, setShowGrant] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [showChangeEmail, setShowChangeEmail] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [confirmReactivate, setConfirmReactivate] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);
  const [portalError, setPortalError] = useState("");

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 2400); };

  const load = async () => {
    try {
      const [o, h] = await Promise.all([
        api.get(`/owners/${ownerId}`),
        api.get(`/owners/${ownerId}/horses`),
      ]);
      setOwner(o.data);
      setHorses(h.data);
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  useEffect(() => { load(); }, [ownerId]);

  const startEdit = () => {
    setForm({
      full_name: owner.full_name || "",
      email: owner.email || "",
      phone: owner.phone || "",
      nationality: owner.nationality || "",
      fei_number: owner.fei_number || "",
      notes: owner.notes || "",
      portal_access: !!owner.portal_access,
    });
    setSaveError("");
    setEditing(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveError("");
    try {
      // Send every form field, remapping empty strings to `null` so the backend
      // clears them (BUG-008b). portal_access is handled via dedicated endpoints.
      const payload = Object.fromEntries(
        Object.entries(form)
          .filter(([k]) => k !== "portal_access")
          .map(([k, v]) => [k, v === "" ? null : v])
      );
      await api.patch(`/owners/${ownerId}`, payload);
      setEditing(false);
      setToast("Changes saved");
      setTimeout(() => setToast(""), 2200);
      await load();
    } catch (err) {
      setSaveError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  // ---- Portal access handlers ----
  const doGrant = async (payload) => {
    setPortalBusy(true); setPortalError("");
    try {
      await api.post(`/owners/${ownerId}/portal-access/grant`, payload);
      setShowGrant(false);
      showToast("Portal access granted. Share the credentials directly with the owner.");
      await load();
    } catch (err) { setPortalError(formatApiError(err)); }
    finally { setPortalBusy(false); }
  };
  const doRevoke = async () => {
    try {
      await api.post(`/owners/${ownerId}/portal-access/revoke`);
      setConfirmRevoke(false);
      showToast("Portal access revoked. The user has been logged out.");
      await load();
    } catch (err) { setError(formatApiError(err)); setConfirmRevoke(false); }
  };
  const doReactivate = async () => {
    try {
      await api.post(`/owners/${ownerId}/portal-access/grant`, {});
      setConfirmReactivate(false);
      showToast("Portal access re-activated.");
      await load();
    } catch (err) { setError(formatApiError(err)); setConfirmReactivate(false); }
  };
  const doReset = async (payload) => {
    setPortalBusy(true); setPortalError("");
    try {
      await api.post(`/owners/${ownerId}/portal-access/reset-password`, payload);
      setShowReset(false);
      showToast("Password updated. Share the new password with the owner directly.");
      await load();
    } catch (err) { setPortalError(formatApiError(err)); }
    finally { setPortalBusy(false); }
  };
  const doChangeLoginEmail = async (newEmail) => {
    setPortalBusy(true); setPortalError("");
    try {
      await api.patch(`/users/${owner.user_id}`, { email: newEmail });
      setShowChangeEmail(false);
      showToast("Login email updated. Owner must use the new email on next login.");
      await load();
    } catch (err) { setPortalError(formatApiError(err)); }
    finally { setPortalBusy(false); }
  };

  const attemptDelete = () => {
    if (horses && horses.length > 0) {
      setDeleteWarning(`This owner has ${horses.length} horse${horses.length === 1 ? "" : "s"} assigned. Please reassign or remove their horses first.`);
      return;
    }
    setConfirmDel(true);
  };

  const doDelete = async () => {
    try {
      await api.delete(`/owners/${ownerId}`);
      navigate("/owners");
    } catch (e) {
      setError(formatApiError(e));
      setConfirmDel(false);
    }
  };

  return (
    <div data-testid="owner-profile-page">
      <button
        onClick={() => navigate("/owners")}
        className="se-btn se-btn-ghost mb-6"
        data-testid="owner-profile-back"
      >
        <ArrowLeft size={16} /> Back to owners
      </button>

      {error && (
        <div className="se-card mb-4" data-testid="owner-profile-error" style={{ color: "#922B21" }}>{error}</div>
      )}
      {toast && (
        <div
          className="mb-4 se-card flex items-center gap-2"
          style={{ background: "rgba(74,124,89,0.12)", color: "#2D5E3A" }}
          data-testid="owner-profile-toast"
        >
          <Save size={14} /> {toast}
        </div>
      )}
      {deleteWarning && (
        <div
          className="mb-4 se-card flex items-start justify-between gap-3"
          style={{ background: "rgba(232,168,56,0.12)", color: "#B8610A" }}
          data-testid="owner-delete-warning"
        >
          <div>{deleteWarning}</div>
          <button className="se-btn se-btn-ghost" onClick={() => setDeleteWarning("")} style={{ padding: 6 }}>
            <X size={14} />
          </button>
        </div>
      )}

      {owner && !editing && (
        <div className="se-card mb-6">
          <div className="flex items-start gap-5">
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0"
              style={{ background: "#7C5C3A", color: "#F5EFE6" }}
            >
              <UserRound size={24} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="se-label">Owner profile</div>
              <h1 className="font-heading mt-1" style={{ fontSize: 28 }} data-testid="owner-profile-name">
                {owner.full_name}
              </h1>
              <div className="flex flex-wrap gap-x-6 gap-y-2 mt-3 text-sm" style={{ color: "#8C9E8A" }}>
                {owner.email && <span className="inline-flex items-center gap-2"><Mail size={14} /> {owner.email}</span>}
                {owner.phone && <span className="inline-flex items-center gap-2"><Phone size={14} /> {owner.phone}</span>}
                {owner.nationality && <span className="inline-flex items-center gap-2"><Globe size={14} /> {owner.nationality}</span>}
                {owner.fei_number && (
                  <span className="inline-flex items-center gap-2" data-testid="owner-fei-number">
                    <span style={{ fontWeight: 600, color: "#7C5C3A" }}>FEI#</span> {owner.fei_number}
                  </span>
                )}
              </div>
            </div>
            <span className={`se-badge ${owner.portal_access ? "se-badge-active" : "se-badge-inactive"}`}>
              Portal {owner.portal_access ? "on" : "off"}
            </span>
          </div>

          {owner.notes && (
            <>
              <div className="se-divider my-5" />
              <div className="se-label mb-2">Notes</div>
              <p className="text-sm">{owner.notes}</p>
            </>
          )}

          <div className="se-divider my-5" />
          <div className="flex gap-2 justify-end">
            {canEdit && (
              <button className="se-btn" onClick={startEdit} data-testid="owner-edit-button">
                <Edit size={14} /> Edit
              </button>
            )}
            {canDelete && (
              <button
                className="se-btn"
                style={{ background: "#C0392B" }}
                onClick={attemptDelete}
                data-testid="owner-delete-button"
              >
                <Trash2 size={14} /> Delete
              </button>
            )}
          </div>
        </div>
      )}

      {owner && editing && (
        <form className="se-card mb-6" onSubmit={save} data-testid="owner-edit-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading" style={{ fontSize: 22 }}>Edit owner</h3>
            <button type="button" className="se-btn se-btn-ghost" onClick={() => setEditing(false)} data-testid="owner-edit-cancel">
              <X size={14} /> Cancel
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <label className="se-label block mb-1">Full name *</label>
              <input className="se-input" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required data-testid="owner-edit-name" />
            </div>
            <div>
              <label className="se-label block mb-1">Email</label>
              <input className="se-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="owner-edit-email" />
            </div>
            <div>
              <label className="se-label block mb-1">Phone</label>
              <input className="se-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="owner-edit-phone" />
            </div>
            <div>
              <label className="se-label block mb-1">Nationality</label>
              <input className="se-input" value={form.nationality} onChange={(e) => setForm({ ...form, nationality: e.target.value })} data-testid="owner-edit-nationality" />
            </div>
            <div>
              <label className="se-label block mb-1">FEI #</label>
              <input
                className="se-input"
                value={form.fei_number}
                onChange={(e) => setForm({ ...form, fei_number: e.target.value })}
                placeholder="Optional"
                data-testid="owner-edit-fei-number"
              />
            </div>
            <div className="md:col-span-2">
              <label className="se-label block mb-1">Notes</label>
              <textarea
                className="se-input"
                style={{ height: 80, padding: "10px 14px" }}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                data-testid="owner-edit-notes"
              />
            </div>
          </div>

          {saveError && (
            <div className="mt-4 text-sm px-3 py-2 rounded-lg" style={{ background: "rgba(192,57,43,0.1)", color: "#922B21" }} data-testid="owner-edit-error">
              {saveError}
            </div>
          )}

          <div className="flex justify-end gap-3 mt-5">
            <button type="button" className="se-btn se-btn-ghost" onClick={() => setEditing(false)}>Cancel</button>
            <button type="submit" className="se-btn" disabled={saving} data-testid="owner-edit-submit">
              {saving ? "Saving…" : (<><Save size={14} /> Save changes</>)}
            </button>
          </div>
        </form>
      )}

      {owner && !editing && canManagePortal && (
        <PortalAccessSection
          owner={owner}
          onGrant={() => { setPortalError(""); setShowGrant(true); }}
          onReactivate={() => setConfirmReactivate(true)}
          onRevoke={() => setConfirmRevoke(true)}
          onReset={() => { setPortalError(""); setShowReset(true); }}
          onChangeEmail={() => { setPortalError(""); setShowChangeEmail(true); }}
        />
      )}

      {owner && (
        <>
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="se-label">Roster</div>
              <h2 className="font-heading" style={{ fontSize: 22 }} data-testid="owner-horses-count">
                {horses ? `${horses.length} horse${horses.length === 1 ? "" : "s"}` : "—"}
              </h2>
            </div>
          </div>

          <div className="se-card p-0 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="se-table w-full" data-testid="owner-horses-table">
                <thead>
                  <tr>
                    <th>Name</th><th>Breed</th><th>Stall</th><th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {horses === null && <tr><td colSpan={4} style={{ color: "#8C9E8A" }}>Loading…</td></tr>}
                  {horses !== null && horses.length === 0 && (
                    <tr><td colSpan={4} style={{ color: "#8C9E8A" }} data-testid="owner-horses-empty">No horses linked to this owner yet.</td></tr>
                  )}
                  {(horses || []).map((h) => (
                    <tr key={h.id} onClick={() => navigate(`/horses/${h.id}`)} style={{ cursor: "pointer" }} data-testid={`owner-horse-row-${h.id}`}>
                      <td style={{ fontWeight: 600 }}>{h.name}</td>
                      <td>{h.breed || "—"}</td>
                      <td>{h.stall_number || "—"}</td>
                      <td><span className={statusToBadge(h.status)}>{h.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <ConfirmDialog
        open={confirmDel}
        onClose={() => setConfirmDel(false)}
        onConfirm={doDelete}
        title={`Delete ${owner?.full_name}?`}
        description="This owner will be moved to trash. A super-admin can restore them later."
        confirmLabel="Yes, delete"
        testid="owner-delete-confirm"
      />

      <ConfirmDialog
        open={confirmRevoke}
        onClose={() => setConfirmRevoke(false)}
        onConfirm={doRevoke}
        title={`Revoke portal access for ${owner?.full_name}?`}
        description="They will be logged out immediately and cannot log back in. Their owner record and all linked data are preserved."
        confirmLabel="Yes, revoke"
        testid="owner-portal-revoke-confirm"
      />

      <ConfirmDialog
        open={confirmReactivate}
        onClose={() => setConfirmReactivate(false)}
        onConfirm={doReactivate}
        title={`Re-activate login for ${owner?.full_name}?`}
        description="The existing credentials remain. They will be able to log in again immediately."
        confirmLabel="Yes, re-activate"
        testid="owner-portal-reactivate-confirm"
      />

      {showGrant && (
        <GrantPortalModal
          owner={owner}
          busy={portalBusy}
          error={portalError}
          onClose={() => setShowGrant(false)}
          onSubmit={doGrant}
        />
      )}

      {showReset && (
        <ResetPortalPasswordModal
          owner={owner}
          busy={portalBusy}
          error={portalError}
          onClose={() => setShowReset(false)}
          onSubmit={doReset}
        />
      )}

      {showChangeEmail && (
        <ChangeLoginEmailModal
          owner={owner}
          busy={portalBusy}
          error={portalError}
          onClose={() => setShowChangeEmail(false)}
          onSubmit={doChangeLoginEmail}
        />
      )}
    </div>
  );
}

// =====================================================================
// PORTAL ACCESS subcomponents
// =====================================================================
function PortalAccessSection({ owner, onGrant, onReactivate, onRevoke, onReset, onChangeEmail }) {
  // Single source of truth: backend-resolved portal_state.
  // Fallback for older API responses: derive from individual flags.
  const state = owner.portal_state || (
    !owner.user_id
      ? (owner.portal_access ? "orphaned" : "not_granted")
      : owner.portal_user_status === "active"
        ? "active"
        : owner.portal_user_status === "inactive"
          ? "inactive"
          : "orphaned"
  );

  const isActive   = state === "active";
  const isInactive = state === "inactive";
  const isOrphan   = state === "orphaned";
  const notGranted = state === "not_granted";

  let statusLabel, statusBg, statusFg, StatusIcon;
  if (isActive)        { statusLabel = "Active";       statusBg = "rgba(74,124,89,0.18)";  statusFg = "#2D5E3A"; StatusIcon = ShieldCheck; }
  else if (isInactive) { statusLabel = "Inactive";     statusBg = "rgba(192,57,43,0.12)";  statusFg = "#922B21"; StatusIcon = ShieldOff;   }
  else if (isOrphan)   { statusLabel = "Broken link";  statusBg = "rgba(192,57,43,0.18)";  statusFg = "#922B21"; StatusIcon = ShieldOff;   }
  else                 { statusLabel = "Not granted";  statusBg = "rgba(140,158,138,0.18)"; statusFg = "#5B6B5B"; StatusIcon = Shield;     }

  return (
    <div className="se-card mb-6" data-testid="owner-portal-section">
      {isOrphan && (
        <div
          className="mb-4 p-3 rounded-lg flex items-start gap-2 text-sm"
          style={{ background: "rgba(192,57,43,0.1)", color: "#922B21", border: "0.5px solid rgba(192,57,43,0.35)" }}
          data-testid="owner-portal-orphan-banner"
        >
          <ShieldOff size={16} style={{ marginTop: 2, flexShrink: 0 }} />
          <span>
            <strong>Portal access is broken</strong> — the linked login account no longer exists.
            Click &quot;Set up portal access&quot; below to create a fresh login for this owner.
          </span>
        </div>
      )}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: statusBg, color: statusFg }}>
            <StatusIcon size={18} />
          </div>
          <div>
            <div className="se-label">Owner portal</div>
            <h3 className="font-heading mt-1" style={{ fontSize: 20 }}>Login access</h3>
            <div className="text-sm mt-1 flex flex-wrap gap-x-4 gap-y-1" style={{ color: "#8C9E8A" }}>
              <span data-testid="owner-portal-status">
                Status: <span style={{ color: statusFg, fontWeight: 600 }}>{statusLabel}</span>
              </span>
              {!isOrphan && owner.portal_user_email && (
                <span data-testid="owner-portal-email" className="inline-flex items-center gap-1">
                  Email: {owner.portal_user_email}
                  {(isActive || isInactive) && (
                    <button
                      type="button"
                      onClick={onChangeEmail}
                      className="inline-flex items-center"
                      style={{ padding: "2px 6px", borderRadius: 6, marginLeft: 4 }}
                      title="Change login email"
                      data-testid="owner-portal-change-email-button"
                    >
                      <Edit size={12} />
                    </button>
                  )}
                </span>
              )}
              {!isOrphan && (isActive || isInactive) && (
                <span data-testid="owner-portal-last-login">
                  {owner.portal_user_last_login_at
                    ? `Last login: ${new Date(owner.portal_user_last_login_at).toLocaleString()}`
                    : "Never logged in"}
                </span>
              )}
              {!isOrphan && owner.portal_user_force_password_change && (
                <span style={{ color: "#B8610A" }}>· Forced password change pending</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {notGranted && (
            <button className="se-btn" onClick={onGrant} data-testid="owner-portal-grant-button"><ShieldCheck size={14} /> Grant portal access</button>
          )}
          {isOrphan && (
            <button className="se-btn" onClick={onGrant} data-testid="owner-portal-setup-button"><ShieldCheck size={14} /> Set up portal access</button>
          )}
          {isActive && (
            <>
              <button className="se-btn" onClick={onReset} data-testid="owner-portal-reset-button"><KeyRound size={14} /> Reset password</button>
              <button className="se-btn" style={{ background: "#C0392B" }} onClick={onRevoke} data-testid="owner-portal-revoke-button"><ShieldOff size={14} /> Revoke access</button>
            </>
          )}
          {isInactive && (
            <>
              <button className="se-btn" onClick={onReactivate} data-testid="owner-portal-reactivate-button"><ShieldCheck size={14} /> Re-activate</button>
              <button className="se-btn se-btn-ghost" onClick={onReset} data-testid="owner-portal-reset-button"><KeyRound size={14} /> Reset password</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function PortalModalShell({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center p-4" style={{ background: "rgba(59,47,30,0.5)" }} onClick={onClose}>
      <div className="se-card w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading" style={{ fontSize: 20 }}>{title}</h3>
          <button type="button" className="se-btn se-btn-ghost" onClick={onClose} style={{ padding: 6 }}><X size={14} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function GrantPortalModal({ owner, busy, error, onClose, onSubmit }) {
  const [email, setEmail] = useState(owner.portal_user_email || owner.email || "");
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [force, setForce] = useState(false);
  const [localErr, setLocalErr] = useState("");
  const submit = (e) => {
    e.preventDefault();
    setLocalErr("");
    if (!email) return setLocalErr("Login email is required.");
    if (pw.length < 8) return setLocalErr("Password must be at least 8 characters.");
    if (pw !== confirm) return setLocalErr("Passwords do not match.");
    onSubmit({ email: email.trim().toLowerCase(), password: pw, force_password_change: force });
  };
  return (
    <PortalModalShell title={`Grant portal access — ${owner.full_name}`} onClose={onClose} testid="owner-portal-grant-form">
      <form onSubmit={submit}>
        <label className="se-label block mb-1">Login email *</label>
        <input type="email" className="se-input mb-3" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="owner-portal-grant-email" />
        <label className="se-label block mb-1">Password * (min 8 characters)</label>
        <input type="password" className="se-input mb-3" required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="new-password" data-testid="owner-portal-grant-password" />
        <label className="se-label block mb-1">Confirm password *</label>
        <input type="password" className="se-input mb-3" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" data-testid="owner-portal-grant-confirm" />
        <label className="flex items-center gap-2 mb-3 cursor-pointer text-sm">
          <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} data-testid="owner-portal-grant-force" />
          Require password change at next login
        </label>
        <p className="text-xs mb-3" style={{ color: "#8C9E8A" }}>No email will be sent. Share the credentials with the owner directly.</p>
        {(localErr || error) && (
          <div className="text-sm px-3 py-2 rounded-lg mb-3" style={{ background: "rgba(192,57,43,0.1)", color: "#922B21" }} data-testid="owner-portal-grant-error">
            {localErr || error}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" className="se-btn se-btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="se-btn" disabled={busy} data-testid="owner-portal-grant-submit"><Save size={14} /> {busy ? "Granting…" : "Grant access"}</button>
        </div>
      </form>
    </PortalModalShell>
  );
}

function ResetPortalPasswordModal({ owner, busy, error, onClose, onSubmit }) {
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [force, setForce] = useState(false);
  const [localErr, setLocalErr] = useState("");
  const submit = (e) => {
    e.preventDefault();
    setLocalErr("");
    if (pw.length < 8) return setLocalErr("Password must be at least 8 characters.");
    if (pw !== confirm) return setLocalErr("Passwords do not match.");
    onSubmit({ new_password: pw, force_password_change: force });
  };
  return (
    <PortalModalShell title={`Reset password — ${owner.full_name}`} onClose={onClose} testid="owner-portal-reset-form">
      <form onSubmit={submit}>
        <label className="se-label block mb-1">New password *</label>
        <input type="password" className="se-input mb-3" required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="new-password" data-testid="owner-portal-reset-password" />
        <label className="se-label block mb-1">Confirm new password *</label>
        <input type="password" className="se-input mb-3" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" data-testid="owner-portal-reset-confirm" />
        <label className="flex items-center gap-2 mb-3 cursor-pointer text-sm">
          <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} data-testid="owner-portal-reset-force" />
          Require password change at next login
        </label>
        {(localErr || error) && (
          <div className="text-sm px-3 py-2 rounded-lg mb-3" style={{ background: "rgba(192,57,43,0.1)", color: "#922B21" }} data-testid="owner-portal-reset-error">
            {localErr || error}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" className="se-btn se-btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="se-btn" disabled={busy} data-testid="owner-portal-reset-submit"><Save size={14} /> {busy ? "Updating…" : "Update password"}</button>
        </div>
      </form>
    </PortalModalShell>
  );
}

function ChangeLoginEmailModal({ owner, busy, error, onClose, onSubmit }) {
  const [email, setEmail] = useState(owner.portal_user_email || "");
  const [localErr, setLocalErr] = useState("");
  const submit = (e) => {
    e.preventDefault();
    setLocalErr("");
    const clean = email.trim().toLowerCase();
    if (!clean) return setLocalErr("Login email is required.");
    if (clean === (owner.portal_user_email || "").toLowerCase()) return setLocalErr("New email is the same as the current one.");
    onSubmit(clean);
  };
  return (
    <PortalModalShell title={`Change login email — ${owner.full_name}`} onClose={onClose} testid="owner-portal-change-email-form">
      <form onSubmit={submit}>
        <label className="se-label block mb-1">Current login email</label>
        <div className="se-input mb-3" style={{ background: "rgba(140,158,138,0.08)", color: "#5B6B5B" }}>
          {owner.portal_user_email || "—"}
        </div>
        <label className="se-label block mb-1">New login email *</label>
        <input
          type="email"
          className="se-input mb-3"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="off"
          data-testid="owner-portal-change-email-input"
        />
        <p className="text-xs mb-3" style={{ color: "#8C9E8A" }}>
          The owner will need to use the new email to log in. Their current session stays active until they log out or the token expires.
          This does not change their contact email above.
        </p>
        {(localErr || error) && (
          <div className="text-sm px-3 py-2 rounded-lg mb-3" style={{ background: "rgba(192,57,43,0.1)", color: "#922B21" }} data-testid="owner-portal-change-email-error">
            {localErr || error}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" className="se-btn se-btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="se-btn" disabled={busy} data-testid="owner-portal-change-email-submit">
            <Save size={14} /> {busy ? "Updating…" : "Update login email"}
          </button>
        </div>
      </form>
    </PortalModalShell>
  );
}
